import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Compass, Award, Globe2, Rocket } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSocialMeta } from "@/hooks/useSocialMeta";
import { listApps, getNewlyListedApps } from "@/lib/api";
import AppCard from "@/components/AppCard";
import SponsorBanner from "@/components/SponsorBanner";
import AdSlot from "@/components/AdSlot";
import TrendingStrip from "@/components/TrendingStrip";
import JsonLd from "@/components/JsonLd";
import { HOME } from "@/constants/testIds";
import { CATEGORY_COLORS, categoryColor } from "@/constants/categoryColors";

const CATEGORY_IMAGES = {
  competitions:
    "https://images.pexels.com/photos/15551477/pexels-photo-15551477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  stable:
    "https://images.pexels.com/photos/14916871/pexels-photo-14916871.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  health:
    "https://images.pexels.com/photos/7882317/pexels-photo-7882317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const HomePage = () => {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);
  const [newlyListed, setNewlyListed] = useState([]);
  const [stats, setStats] = useState({ apps: 0, features: 50, regions: 4 });

  useSocialMeta({
    title: `${t.hero?.title || "Compare every horse app in the world."} | HorseAppCompare`,
    description: t.hero?.lede || "Objective profiles, side-by-side comparisons and AI-powered recommendations for the global equestrian community.",
    type: "website",
  });

  useEffect(() => {
    listApps({ sort: "score", limit: 6 })
      .then((data) => setFeatured(data))
      .catch(() => setFeatured([]));
    listApps({ limit: 100 }).then((data) => {
      const feats = new Set();
      data.forEach((a) => (a.features || []).forEach((f) => feats.add(f)));
      setStats({ apps: data.length, features: feats.size, regions: 4 });
    });
    getNewlyListedApps(4)
      .then((d) => setNewlyListed(d?.apps || []))
      .catch(() => setNewlyListed([]));
  }, []);

  const categoryShowcase = [
    { key: "competitions", image: CATEGORY_IMAGES.competitions },
    { key: "stable", image: CATEGORY_IMAGES.stable },
    { key: "health", image: CATEGORY_IMAGES.health },
  ];

  return (
    <main>
      <JsonLd
        id="home-website"
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "HorseAppCompare",
          alternateName: "Horse App Compare",
          url: "https://horseappcompare.com/",
          inLanguage: lang,
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://horseappcompare.com/apps?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <JsonLd
        id="home-org"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "HorseAppCompare",
          url: "https://horseappcompare.com/",
          logo: "https://horseappcompare.com/og-default.png",
          description: "The global directory of horse apps and equine software.",
          sameAs: [
            "https://twitter.com/horseappcompare",
          ],
        }}
      />
      {/* ---- Featured App of the Week (auction winner) — top placement ---- */}
      <AdSlot placement="homepage-featured" />

      {/* ---- Global onboarding announcement strip ---- */}
      <Link
        to="/submit-app"
        data-testid="home-onboarding-strip"
        className="group relative block overflow-hidden bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white border-y border-[#1E293B] hover:from-[#1E293B] hover:via-[#0F172A] hover:to-[#1E293B] transition-colors"
      >
        {/* subtle moving gradient orb */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 50%, #EA580C 0%, transparent 35%), radial-gradient(circle at 88% 50%, #2563EB 0%, transparent 35%)",
          }}
          aria-hidden
        />
        <div className="hac-container relative py-3 md:py-3.5 flex items-center justify-center gap-3 md:gap-5 text-center flex-wrap">
          <span className="inline-flex items-center gap-1.5 bg-[#EA580C]/15 border border-[#EA580C]/40 text-[#FDBA74] px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]">
            <Globe2 size={12} className="animate-pulse" />
            {t.onboarding?.badge || "Now live worldwide"}
          </span>
          <span className="text-sm md:text-base font-medium text-white/95">
            {t.onboarding?.lead || "Now onboarding global apps —"}{" "}
            <span className="text-[#FDBA74] font-bold">
              {t.onboarding?.highlight || "claim your free position"}
            </span>
            .
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-[#FDBA74] transition-colors">
            <Rocket size={14} />
            {t.onboarding?.cta || "Register app"}
            <ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </span>
        </div>
      </Link>

      {/* ---- HERO ---- */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, #2563EB 0%, transparent 40%), radial-gradient(circle at 80% 70%, #06B6D4 0%, transparent 40%)",
          }}
          aria-hidden
        />
        <div className="hac-container relative py-20 md:py-32 max-w-5xl">
          <div className="overline mb-6 hac-fade-up text-[#2563EB]">{t.hero.eyebrow}</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#0F172A] leading-[0.95] mb-6 hac-fade-up hac-delay-1">
            {t.hero.title}
          </h1>
          <p className="text-lg md:text-xl text-[#64748B] max-w-2xl leading-relaxed mb-10 hac-fade-up hac-delay-2">
            {t.hero.lede}
          </p>
          <div className="flex flex-wrap gap-3 hac-fade-up hac-delay-3">
            <Link to="/apps" data-testid={HOME.ctaBrowse} className="hac-btn-primary">
              {t.hero.ctaBrowse}
              <ArrowRight size={16} />
            </Link>
            <Link to="/advisor" data-testid={HOME.ctaAdvisor} className="hac-btn-secondary">
              <Sparkles size={16} />
              {t.hero.ctaAdvisor}
            </Link>
          </div>

          <dl className="grid grid-cols-3 gap-8 mt-16 max-w-2xl hac-fade-up hac-delay-3">
            {[
              { value: stats.apps, label: t.hero.stat1Label },
              { value: `${stats.features}+`, label: t.hero.stat2Label },
              { value: stats.regions, label: t.hero.stat3Label },
            ].map((s) => (
              <div key={s.label}>
                <dt className="font-heading text-4xl md:text-5xl text-[#0F172A] leading-none">
                  {s.value}
                </dt>
                <dd className="text-xs text-[#64748B] uppercase tracking-[0.18em] mt-2">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <AdSlot placement="homepage-banner" />

      <TrendingStrip />

      {/* ---- Cinematic video ad placement (full browser width) ---- */}
      <AdSlot placement="homepage-video" />

      {/* ---- Featured Apps ---- */}
      <section className="hac-container py-20 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline mb-3 text-[#2563EB]">
              <Award size={12} className="inline mr-2" />
              {t.sections.featured}
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight max-w-2xl">
              {t.sections.featuredSub}
            </h2>
          </div>
          <Link
            to="/rankings"
            className="text-sm text-[#2563EB] hover:text-[#1D4ED8] transition-colors flex items-center gap-1 font-medium"
          >
            {t.nav.rankings} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((a) => (
            <AppCard key={a.slug} app={a} />
          ))}
        </div>

        <AdSlot placement="homepage-square" />

        <Link
          to="/best-horse-apps"
          className="mt-10 block bg-[#0F172A] text-white rounded-2xl p-6 md:p-8 hover:bg-[#1E293B] transition-colors"
          data-testid="home-seo-card"
        >
          <div className="flex items-start gap-4 md:items-center md:flex-row flex-col">
            <div className="w-12 h-12 shrink-0 bg-[#2563EB] rounded-full flex items-center justify-center">
              <Award size={22} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#60A5FA] mb-1">
                Editorial guide · Updated monthly
              </div>
              <h3 className="text-xl md:text-2xl font-bold leading-tight">
                Best Horse Apps in 2026 — Read the full comparison guide
              </h3>
              <p className="text-sm text-white/70 mt-2 max-w-2xl">
                Long-form review of 25+ horse apps across riding tracking, management,
                health, stable software and AI coaching. FAQs, personas and feature explanations.
              </p>
            </div>
            <ArrowRight size={20} className="text-white/60 hidden md:block" />
          </div>
        </Link>
      </section>

      {/* ---- Just listed this week ---- */}
      {newlyListed.length > 0 && (
        <section className="hac-container mt-20" data-testid="home-newly-listed-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="overline mb-3 text-emerald-600 flex items-center gap-2">
                <Sparkles size={12} />
                {t.sections?.newlyListed || "Just listed this week"}
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight">
                {t.sections?.newlyListedHeadline || "Fresh apps to discover"}
              </h2>
              <p className="text-[#64748B] text-sm md:text-base mt-3 max-w-xl">
                {t.sections?.newlyListedSub ||
                  "Fresh apps that recently joined the platform — be among the first to discover them."}
              </p>
            </div>
            <Link
              to="/apps?sort=newest"
              className="text-sm font-medium text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 group"
              data-testid="home-newly-listed-cta"
            >
              {lang === "nl" ? "Bekijk alle" : "View all"}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-newly-listed-grid">
            {newlyListed.map((a) => (
              <AppCard key={a.slug} app={a} />
            ))}
          </div>
        </section>
      )}

      <SponsorBanner />

      {/* ---- Categories showcase ---- */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-20 md:py-24 mt-20">
        <div className="hac-container">
          <div className="mb-12 max-w-2xl">
            <div className="overline mb-3 text-[#2563EB]">
              <Compass size={12} className="inline mr-2" />
              {t.sections.categories}
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight">
              {t.sections.categoriesSub}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {categoryShowcase.map((c) => (
              <Link
                key={c.key}
                to={`/categories/${c.key}`}
                className="group relative h-72 overflow-hidden rounded-2xl border border-[#E2E8F0] block"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${c.image})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to top, ${categoryColor(c.key)}EE 0%, ${categoryColor(c.key)}40 60%, transparent 100%)`,
                  }}
                />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <div className="overline text-white/80 mb-2">{t.nav.categories}</div>
                  <div className="font-heading text-3xl leading-tight">{t.categories[c.key]}</div>
                  <div className="mt-3 text-xs flex items-center gap-1 opacity-80 group-hover:opacity-100 transition">
                    {t.common.overview} <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
            {Object.entries(t.categories).map(([k, v]) => {
              const color = categoryColor(k);
              return (
                <Link
                  key={k}
                  to={`/categories/${k}`}
                  className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-4 text-sm text-[#0F172A] hover:border-current transition-colors flex flex-col items-center text-center gap-2"
                  style={{ color }}
                  data-testid={`home-category-${k}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  <span className="text-[#0F172A] font-medium leading-tight">{v}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- Advisor CTA ---- */}
      <section className="hac-container py-20 md:py-28">
        <div className="border border-[#E2E8F0] bg-white rounded-2xl p-8 md:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-3 text-[#2563EB]">
              <Sparkles size={12} className="inline mr-2" />
              Advisor
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-4">
              {t.sections.advisor}
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-6">{t.sections.advisorSub}</p>
            <Link to="/advisor" className="hac-btn-primary">
              <Sparkles size={16} />
              {t.hero.ctaAdvisor}
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-6 md:p-8 space-y-3">
            {t.advisor.examples.map((ex, i) => (
              <Link
                key={i}
                to="/advisor"
                state={{ prefill: ex }}
                className="block text-sm border border-transparent hover:border-[#E2E8F0] hover:bg-white rounded-lg p-3 transition-all"
              >
                <span className="text-[#2563EB] mr-2 font-mono text-xs">0{i + 1}</span>
                {ex}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---- SEO index: A-Z directory of every app (server-rendered for Google) ---- */}
      <AppsAZBlock />
    </main>
  );
};

/**
 * A-Z block of every active app, rendered as plain anchor tags so Googlebot
 * can crawl every directory URL in one hop from the homepage. Loads from
 * /api/apps with a generous limit; sorts alphabetically.
 */
const AppsAZBlock = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [apps, setApps] = useState([]);

  useEffect(() => {
    let cancelled = false;
    listApps({ limit: 500, sort: "name" })
      .then((d) => { if (!cancelled) setApps(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setApps([]); });
    return () => { cancelled = true; };
  }, []);

  if (apps.length === 0) return null;

  const grouped = apps.reduce((acc, a) => {
    const letter = ((a.name || "?").charAt(0) || "?").toUpperCase();
    const key = /^[A-Z]$/.test(letter) ? letter : "#";
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});
  const letters = Object.keys(grouped).sort();

  return (
    <section className="hac-container py-16 border-t border-[#E2E8F0]" data-testid="home-az-directory">
      <div className="mb-8">
        <div className="overline mb-3 text-[#64748B]">
          {isNl ? "Volledige index" : "Full index"}
        </div>
        <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] leading-tight">
          {isNl ? "Alle horse-apps van A tot Z" : "Every horse app from A to Z"}
        </h2>
        <p className="text-sm text-[#64748B] mt-2 max-w-2xl">
          {isNl
            ? `Compleet overzicht van alle ${apps.length} geregistreerde apps. Klik op een naam voor de volledige review, prijsplannen en alternatieven.`
            : `Complete overview of all ${apps.length} listed apps. Click any name for the full review, pricing plans and alternatives.`}
        </p>
      </div>
      <div className="space-y-6">
        {letters.map((letter) => (
          <div key={letter} className="grid grid-cols-[40px_1fr] gap-4 items-start">
            <div className="font-heading text-2xl text-[#2563EB] tabular-nums">{letter}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {grouped[letter].map((a) => (
                <Link
                  key={a.slug}
                  to={`/apps/${a.slug}`}
                  className="text-sm text-[#0F172A] hover:text-[#2563EB] hover:underline transition-colors"
                  data-testid={`az-link-${a.slug}`}
                >
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HomePage;
